import type { NativeResolveErrorCode } from './nativeMessaging'
import { Strings } from '../../constants/Strings'

export const RESOLVE_ENDPOINT = 'POST /oneapp/resolve-user'

export type ResolveErrorCode =
  | 'JWT_DECODE_FAILED'
  | 'JWT_EXPIRED'
  | 'JWT_INVALID'
  | 'EMAIL_MISMATCH'
  | 'PARTNER_NOT_FOUND'
  | 'PARTNER_INACTIVE'
  | 'PARTNER_CONFIG_ERROR'
  | 'BUILDING_REQUIRED'
  | 'BUILDING_NOT_FOUND'
  | 'BUILDING_MISMATCH'
  | 'USER_NO_BUILDING'
  | 'BUILDING_PENDING_APPROVAL'
  | 'LEASE_INACTIVE'
  | 'ACTIVE_LEASE_ELSEWHERE'
  | 'EXTERNAL_AUTH_ERROR'

export interface ResolveErrorDefinition {
  code: ResolveErrorCode
  status: number
  message: string
  category: string
  userMessage: string
  action: string
  note?: string
}

export const RESOLVE_ERROR_DEFINITIONS: Record<ResolveErrorCode, ResolveErrorDefinition> = {
  JWT_DECODE_FAILED: {
    code: 'JWT_DECODE_FAILED',
    status: 400,
    message: 'Unable to decode JWT token',
    category: 'JWT Validation',
    userMessage: 'Authentication failed. Please try again.',
    action: 'Validate JWT format before sending',
  },
  JWT_EXPIRED: {
    code: 'JWT_EXPIRED',
    status: 401,
    message: 'Token has expired',
    category: 'JWT Validation',
    userMessage: 'Your session has expired. Please refresh and try again.',
    action: 'Request new JWT from Homebody',
  },
  JWT_INVALID: {
    code: 'JWT_INVALID',
    status: 401,
    message: 'Invalid JWT token',
    category: 'JWT Validation',
    userMessage: 'Authentication failed. Please try again.',
    action: 'Request new JWT from Homebody',
  },
  EMAIL_MISMATCH: {
    code: 'EMAIL_MISMATCH',
    status: 400,
    message: 'JWT email does not match payload email',
    category: 'JWT Validation',
    userMessage: 'Authentication error. Please try again.',
    action: 'Ensure JWT and payload emails match',
    note: 'Currently disabled for testing',
  },
  PARTNER_NOT_FOUND: {
    code: 'PARTNER_NOT_FOUND',
    status: 500,
    message: 'Partner configuration not found',
    category: 'Partner Configuration',
    userMessage: 'Service temporarily unavailable. Please contact support.',
    action: 'Contact Cobu support',
  },
  PARTNER_INACTIVE: {
    code: 'PARTNER_INACTIVE',
    status: 403,
    message: 'Partner integration is not active',
    category: 'Partner Configuration',
    userMessage: 'Service temporarily unavailable. Please contact support.',
    action: 'Contact Cobu support',
  },
  PARTNER_CONFIG_ERROR: {
    code: 'PARTNER_CONFIG_ERROR',
    status: 500,
    message: 'Partner public key not configured',
    category: 'Partner Configuration',
    userMessage: 'Service temporarily unavailable. Please contact support.',
    action: 'Contact Cobu support',
    note: 'Will be active when JWT verification is enabled',
  },
  BUILDING_REQUIRED: {
    code: 'BUILDING_REQUIRED',
    status: 400,
    message: 'Building is required to create a new user',
    category: 'Building Validation',
    userMessage: 'Missing building information. Please try again.',
    action: 'Include buildingName or cid+propertyId in payload',
  },
  BUILDING_NOT_FOUND: {
    code: 'BUILDING_NOT_FOUND',
    status: 404,
    message: 'Building not found with the provided cid+propertyId or name',
    category: 'Building Validation',
    userMessage: 'Building not found. Please contact support.',
    action: 'Verify cid+propertyId or buildingName is correct',
  },
  BUILDING_MISMATCH: {
    code: 'BUILDING_MISMATCH',
    status: 403,
    message: "Existing user's building does not match the resolved building.",
    category: 'Building Validation',
    userMessage: "You don't have access to this building.",
    action: 'Security check - user building UUID does not match resolved building (by cid+propertyId or name)',
  },
  USER_NO_BUILDING: {
    code: 'USER_NO_BUILDING',
    status: 400,
    message: 'User does not belong to any building',
    category: 'Building Validation',
    userMessage: 'Account configuration error. Please contact support.',
    action: 'Contact Cobu support',
  },
  BUILDING_PENDING_APPROVAL: {
    code: 'BUILDING_PENDING_APPROVAL',
    status: 409,
    message: 'Building is queued for onboarding and not yet approved',
    category: 'Building Validation',
    userMessage:
      "We're setting things up!\nYour community is in the queue to join Community powered by Cobu. Please check back again soon.",
    action: 'Show the server message as-is; no generic error heading',
  },
  LEASE_INACTIVE: {
    code: 'LEASE_INACTIVE',
    status: 403,
    message: 'Lease for this building has ended.',
    category: 'Lease Validation',
    userMessage: 'Lease for this building has ended.',
    action: 'Show our localized copy under the standard error heading',
    note: 'Fires when the resident moved out, and when they open a second building whose lease already ended',
  },
  ACTIVE_LEASE_ELSEWHERE: {
    code: 'ACTIVE_LEASE_ELSEWHERE',
    status: 403,
    message:
      'We have detected an active account in another building with the same email address. Please log in here once you have moved out of the previous building.',
    category: 'Lease Validation',
    userMessage:
      'We have detected an active account in another building with the same email address. Please log in here once you have moved out of the previous building.',
    action: 'Show our localized copy under the standard error heading',
    note: 'Same email holds an active lease in another building — we neither auto-switch nor deactivate the other account',
  },
  EXTERNAL_AUTH_ERROR: {
    code: 'EXTERNAL_AUTH_ERROR',
    status: 500,
    message: 'Unable to complete external authentication.',
    category: 'General',
    userMessage: 'Something went wrong. Please try again.',
    action: 'Retry once, then contact support if persists',
  },
}

export const RESOLVE_SUCCESS_RESPONSE = {
  status: 200,
  structure: {
    message: 'Token exchange successful',
    user: 'object',
    token: 'string',
    streamToken: 'string',
    intercomJwt: 'string',
    source: 'external',
    partner: 'object',
  },
} as const

export const RESOLVE_ERROR_HANDLING_STRATEGY = {
  retryable: ['EXTERNAL_AUTH_ERROR', 'JWT_INVALID'],
  showToUser: [
    'JWT_EXPIRED',
    'BUILDING_MISMATCH',
    'BUILDING_NOT_FOUND',
    'BUILDING_PENDING_APPROVAL',
    'LEASE_INACTIVE',
    'ACTIVE_LEASE_ELSEWHERE',
  ],
  contactSupport: ['PARTNER_NOT_FOUND', 'PARTNER_INACTIVE', 'PARTNER_CONFIG_ERROR', 'USER_NO_BUILDING'],
  clientValidation: ['JWT_DECODE_FAILED', 'EMAIL_MISMATCH', 'BUILDING_REQUIRED'],
} as const

const RESOLVE_ERROR_TO_NATIVE_CODE: Record<ResolveErrorCode, NativeResolveErrorCode> = {
  JWT_DECODE_FAILED: 'AUTH_FAILED',
  JWT_EXPIRED: 'AUTH_FAILED',
  JWT_INVALID: 'AUTH_FAILED',
  EMAIL_MISMATCH: 'AUTH_FAILED',
  PARTNER_NOT_FOUND: 'SERVER_DOWN',
  PARTNER_INACTIVE: 'SERVER_DOWN',
  PARTNER_CONFIG_ERROR: 'SERVER_DOWN',
  BUILDING_REQUIRED: 'AUTH_FAILED',
  BUILDING_NOT_FOUND: 'INVALID_ACCESS',
  BUILDING_MISMATCH: 'INVALID_ACCESS',
  USER_NO_BUILDING: 'SERVER_DOWN',
  BUILDING_PENDING_APPROVAL: 'INVALID_ACCESS',
  LEASE_INACTIVE: 'INVALID_ACCESS',
  ACTIVE_LEASE_ELSEWHERE: 'INVALID_ACCESS',
  EXTERNAL_AUTH_ERROR: 'SERVER_DOWN',
}

// Codes whose user-facing copy we own and translate. The server's `message` is
// English-only, so for these we key off the code and render our own string
// instead. Read lazily: the session language is applied before resolve runs.
// RESOLVE_ERROR_DEFINITIONS[code].userMessage is the single source of truth for
// the English copy; Strings.es.ts is the only place that should override it.
const RESOLVE_ERROR_LOCALIZED_MESSAGES: Partial<Record<ResolveErrorCode, () => string | undefined>> = {
  LEASE_INACTIVE: () => Strings.get().sessionErrors.leaseInactive,
  ACTIVE_LEASE_ELSEWHERE: () => Strings.get().sessionErrors.activeLeaseElsewhere,
}

export const getLocalizedResolveErrorMessage = (code: ResolveErrorCode): string | undefined => {
  const getMessage = RESOLVE_ERROR_LOCALIZED_MESSAGES[code]
  if (!getMessage) {
    return undefined
  }
  return getMessage() ?? RESOLVE_ERROR_DEFINITIONS[code].userMessage
}

export const isResolveErrorCode = (value: unknown): value is ResolveErrorCode => {
  return typeof value === 'string' && value in RESOLVE_ERROR_DEFINITIONS
}

export const mapResolveErrorCodeToNative = (code: ResolveErrorCode): NativeResolveErrorCode => {
  return RESOLVE_ERROR_TO_NATIVE_CODE[code] ?? 'SERVER_DOWN'
}

export const getResolveErrorDefinition = (code: ResolveErrorCode): ResolveErrorDefinition => {
  return RESOLVE_ERROR_DEFINITIONS[code]
}

